import { useMemo, useState } from 'react';
import type { OrCase, RoomResource } from './useAscSchedule';
import { roomKey, roomDisplay, careTeamLine } from './useAscSchedule';

// ── OR schedule grid ─────────────────────────────────────────────────────────
// Rooms as columns, the day's hours down the side, cases as rich cards:
// Patient · Procedure · Surgeon · time window, status-edged (cyan in
// progress, gold scheduled, green complete, red cancelled). Per-card menu:
// Edit / Reschedule / Cancel — Forge parity. All times LOCAL.

const DAY_START = 7;   // 7:00
const DAY_END = 18;    // 18:00
const PX_PER_MIN = 1.1;

function localHM(ts: string | null): string {
    if (!ts) return '—';
    return new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}
function minutesIntoDay(ts: string): number {
    const d = new Date(ts);
    return (d.getHours() - DAY_START) * 60 + d.getMinutes();
}

type CardTone = 'live' | 'scheduled' | 'done' | 'cancelled';
function toneOf(c: OrCase): CardTone {
    if ((c.case_status ?? c.schedule_status ?? '') === 'cancelled' || (c.schedule_status ?? '') === 'cancelled') return 'cancelled';
    if (c.completed_at != null || (c.case_status ?? '') === 'completed') return 'done';
    if (c.actual_start != null || (c.stage ?? '') === 'inor') return 'live';
    return 'scheduled';
}
const TONE_CLASSES: Record<CardTone, string> = {
    live: 'border-pt-cyan border-l-[3px] border-l-pt-cyan bg-pt-cyan/10',
    scheduled: 'border-pt-gold/50 border-l-[3px] border-l-pt-gold bg-pt-panel',
    done: 'border-emerald-500/50 border-l-[3px] border-l-emerald-500 bg-pt-panel opacity-80',
    cancelled: 'border-red-500/40 border-l-[3px] border-l-red-500 bg-pt-panel opacity-60',
};
const TONE_CHIP: Record<CardTone, { txt: string; cls: string }> = {
    live: { txt: 'IN OR', cls: 'bg-emerald-500/15 text-emerald-400' },
    scheduled: { txt: 'SCHEDULED', cls: 'bg-pt-gold/15 text-pt-gold' },
    done: { txt: 'COMPLETE', cls: 'bg-emerald-500/15 text-emerald-400' },
    cancelled: { txt: 'CANCELLED', cls: 'bg-red-500/15 text-red-400' },
};

export function OrGrid({ cases, rooms, onEdit, onCancel }: {
    cases: OrCase[];
    rooms: RoomResource[];
    onEdit: (c: OrCase) => void;
    onCancel: (c: OrCase) => void;
}) {
    const [menuFor, setMenuFor] = useState<number | null>(null);

    const orRooms = rooms.filter((r) => r.resource_kind === 'or_suite');
    const roomNames = useMemo(() => {
        const byKey = new Map<string, string>();
        for (const r of orRooms) {
            const raw = r.display_name.replace('Operating Room ', 'OR ');
            byKey.set(roomKey(raw), roomDisplay(raw));
        }
        for (const c of cases) {
            if (!c.or_room) continue;
            const k = roomKey(c.or_room);
            if (!byKey.has(k)) byKey.set(k, roomDisplay(c.or_room));
        }
        return Array.from(byKey.values()).sort((a, b) =>
            a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    }, [orRooms, cases]);

    const hours: number[] = [];
    for (let h = DAY_START; h <= DAY_END; h++) hours.push(h);
    const gridHeight = (DAY_END - DAY_START) * 60 * PX_PER_MIN;

    return (
        <section className="rounded-xl border border-pt-border overflow-hidden bg-pt-darker">
            <div className="px-4 py-2.5 border-b border-pt-border bg-pt-panel flex items-center justify-between gap-3">
                <div><h2 className="text-sm font-semibold text-pt-text">Master OR schedule</h2><p className="text-[11px] text-pt-text-muted">Room-protected timeline · drag-free precision · horizontal room navigation</p></div>
                <span className="text-[10px] text-pt-text-dim">{roomNames.length} OR suites</span>
            </div>
            <div className="overflow-x-auto overscroll-x-contain">
            <div className="grid" style={{ gridTemplateColumns: `56px repeat(${roomNames.length || 1}, minmax(210px, 1fr))`, minWidth: `${56 + Math.max(1, roomNames.length) * 210}px` }}>
                <div className="sticky left-0 z-20 bg-pt-panel border-b border-pt-border/60" />
                {roomNames.map((rn) => {
                    const live = cases.some((c) => roomKey(c.or_room) === roomKey(rn) && toneOf(c) === 'live');
                    return (
                        <div key={rn} className="bg-pt-panel border-b border-l border-pt-border/60 px-3 py-2 flex items-baseline gap-2">
                            <span className="text-sm font-medium text-pt-text">{rn}</span>
                            <span className={['text-[11px]', live ? 'text-emerald-400' : 'text-pt-text-muted'].join(' ')}>
                                {live ? '● in use' : '○ open'}
                            </span>
                        </div>
                    );
                })}

                <div className="sticky left-0 z-10 border-r border-pt-border/40 bg-pt-darker" style={{ height: gridHeight }}>
                    {hours.map((h) => (
                        <div key={h} className="absolute right-1.5 -translate-y-1/2 text-[10px] text-pt-text-muted"
                            style={{ top: (h - DAY_START) * 60 * PX_PER_MIN }}>
                            {new Date(2000, 0, 1, h).toLocaleTimeString([], { hour: 'numeric' })}
                        </div>
                    ))}
                </div>

                {roomNames.map((rn) => (
                    <div key={rn} className="relative border-l border-pt-border/30" style={{ height: gridHeight }}>
                        {hours.map((h) => (
                            <div key={h} className="absolute left-0 right-0 border-t border-pt-border/20"
                                style={{ top: (h - DAY_START) * 60 * PX_PER_MIN }} />
                        ))}
                        {cases.filter((c) => roomKey(c.or_room) === roomKey(rn) && c.scheduled_start).map((c) => {
                            const tone = toneOf(c);
                            const top = Math.max(0, minutesIntoDay(c.scheduled_start as string) * PX_PER_MIN);
                            const height = Math.max(46, (c.estimated_duration_minutes ?? 60) * PX_PER_MIN);
                            const chip = TONE_CHIP[tone];
                            return (
                                <div key={c.case_id}
                                    className={['absolute left-1.5 right-1.5 rounded-r-md border px-2.5 py-1.5 overflow-hidden', TONE_CLASSES[tone]].join(' ')}
                                    style={{ top, height }}>
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="text-[13px] font-medium text-pt-text truncate">{c.patient_name ?? `Case #${c.case_id}`}</div>
                                            <div className="text-[11px] text-pt-text-muted truncate">{c.procedure_name ?? 'Procedure TBD'}</div>
                                            <div className="text-[11px] text-pt-text-dim truncate">
                                                {c.surgeon_name ?? '—'} · {localHM(c.scheduled_start)}
                                                {c.estimated_duration_minutes ? ` · ${c.estimated_duration_minutes} min` : ''}
                                            </div>
                                            {careTeamLine(c) && (
                                                <div className="text-[10px] text-pt-text-dim truncate">{careTeamLine(c)}</div>
                                            )}
                                        </div>
                                        <div className="text-right shrink-0">
                                            <span className={['text-[9px] tracking-wider rounded px-1.5 py-0.5', chip.cls].join(' ')}>{chip.txt}</span>
                                            {tone !== 'cancelled' && tone !== 'done' && (
                                                <button type="button" aria-label="Case actions"
                                                    onClick={() => setMenuFor(menuFor === c.case_id ? null : c.case_id)}
                                                    className="block ml-auto mt-1 text-pt-text-muted hover:text-pt-text tracking-[2px] leading-none">⋯</button>
                                            )}
                                        </div>
                                    </div>
                                    {menuFor === c.case_id && (
                                        <div className="absolute right-1 top-9 z-10 w-40 rounded-md border border-pt-border bg-pt-panel py-1">
                                            <button type="button" onClick={() => { setMenuFor(null); onEdit(c); }}
                                                className="w-full text-left text-xs text-pt-text px-3 py-1.5 hover:bg-pt-cyan/10">Edit · reschedule</button>
                                            <button type="button" onClick={() => { setMenuFor(null); onCancel(c); }}
                                                className="w-full text-left text-xs text-red-400 px-3 py-1.5 hover:bg-red-500/10 border-t border-pt-border/50">Cancel case</button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
            </div>
        </section>
    );
}
